import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Timestamp } from 'firebase/firestore';
import { PersonalRecord } from '../../types';
import { minutesToHMS, minutesFromUserParts, normalizeMinutesForStorage } from '../../utils/prTimeFormat';

interface AddEditPRDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (prData: Partial<PersonalRecord>) => Promise<void>;
  existingPR?: PersonalRecord | null;
  isCoachEditing?: boolean;
}

const AddEditPRDialog: React.FC<AddEditPRDialogProps> = ({
  open,
  onClose,
  onSave,
  existingPR,
  isCoachEditing = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventType, setEventType] = useState<'400m' | '800m' | '1600m' | '3200m' | '3k' | '5K' | '10K' | 'Half Marathon' | 'Marathon' | 'Custom'>('5K');
  const [customDistance, setCustomDistance] = useState<number | ''>('');
  const [hours, setHours] = useState<number | ''>('');
  const [minutes, setMinutes] = useState<number | ''>('');
  const [seconds, setSeconds] = useState<number | ''>('');
  const [date, setDate] = useState<Date | null>(new Date());
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (open) {
      if (existingPR) {
        // Populate form with existing PR data
        setEventType(existingPR.eventType);
        setCustomDistance(existingPR.distance || '');
        
        const { h, m, s } = minutesToHMS(existingPR.time);
        
        setHours(h || '');
        setMinutes(m || '');
        setSeconds(s || '');
        setDate(existingPR.date.toDate());
        setLocation(existingPR.location || '');
        setNotes(existingPR.notes || '');
        setIsVerified(existingPR.isVerified || false);
      } else {
        // Reset form
        setEventType('5K');
        setCustomDistance('');
        setHours('');
        setMinutes('');
        setSeconds('');
        setDate(new Date());
        setLocation('');
        setNotes('');
        setIsVerified(isCoachEditing);
      }
      setError('');
    }
  }, [open, existingPR, isCoachEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    if (eventType === 'Custom' && !customDistance) {
      setError('Please enter a distance for custom events');
      return;
    }
    
    if (!hours && !minutes && !seconds) {
      setError('Please enter a valid time');
      return;
    }
    
    if (!date) {
      setError('Please select a date');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Calculate total time in minutes
      const totalMinutes = normalizeMinutesForStorage(
        minutesFromUserParts(Number(hours) || 0, Number(minutes) || 0, Number(seconds) || 0)
      );

      const prData: Partial<PersonalRecord> = {
        eventType,
        time: totalMinutes,
        date: Timestamp.fromDate(date),
        isVerified: isCoachEditing || isVerified,
      };

      if (eventType === 'Custom' && customDistance) {
        prData.distance = Number(customDistance);
      }

      if (location.trim()) {
        prData.location = location.trim();
      }

      if (notes.trim()) {
        prData.notes = notes.trim();
      }

      await onSave(prData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save personal record');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = () => {
    const h = Number(hours) || 0;
    const m = Number(minutes) || 0;
    const s = Number(seconds) || 0;
    
    if (h === 0 && m === 0 && s === 0) return '';
    
    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (s > 0) parts.push(`${s}s`);
    
    return parts.join(' ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {existingPR ? 'Edit' : 'Add'} Personal Record
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Event Type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value as typeof eventType)}
              fullWidth
              required
            >
              <MenuItem value="400m">400m</MenuItem>
              <MenuItem value="800m">800m</MenuItem>
              <MenuItem value="1600m">1600m (Mile)</MenuItem>
              <MenuItem value="3200m">3200m (2 Mile)</MenuItem>
              <MenuItem value="3k">3k</MenuItem>
              <MenuItem value="5K">5K</MenuItem>
              <MenuItem value="10K">10K</MenuItem>
              <MenuItem value="Half Marathon">Half Marathon</MenuItem>
              <MenuItem value="Marathon">Marathon</MenuItem>
              <MenuItem value="Custom">Custom Distance</MenuItem>
            </TextField>

            {eventType === 'Custom' && (
              <TextField
                label="Distance (miles)"
                type="number"
                value={customDistance}
                onChange={(e) => setCustomDistance(e.target.value ? Number(e.target.value) : '')}
                fullWidth
                required
                inputProps={{ min: 0.1, step: 0.1 }}
              />
            )}

            <Box>
              <Box sx={{ mb: 1 }}>
                <TextField
                  label="Time"
                  value={formatTime()}
                  fullWidth
                  disabled
                  placeholder="Enter hours, minutes, and seconds below"
                  helperText="Your race time"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Hours"
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value ? Number(e.target.value) : '')}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
                <TextField
                  label="Minutes"
                  type="number"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value ? Number(e.target.value) : '')}
                  inputProps={{ min: 0, max: 59 }}
                  fullWidth
                />
                <TextField
                  label="Seconds"
                  type="number"
                  value={seconds}
                  onChange={(e) => setSeconds(e.target.value ? Number(e.target.value) : '')}
                  inputProps={{ min: 0, max: 59 }}
                  fullWidth
                />
              </Box>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Race Date"
                value={date}
                onChange={(newDate) => setDate(newDate)}
                maxDate={new Date()}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </LocalizationProvider>

            <TextField
              label="Location (Optional)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              fullWidth
              placeholder="e.g., Boston Marathon"
            />

            <TextField
              label="Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Any additional details about this PR"
            />

            {isCoachEditing && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isVerified}
                    onChange={(e) => setIsVerified(e.target.checked)}
                  />
                }
                label="Verify this PR (Coach verification)"
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditPRDialog;

